import { Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';

import { ToastService } from '../../services/Toast.service';
import { TOAST_TYPE } from '../../constants/Tpiq.constants';
import { Session } from '../../services/Session';
import { LoadingService } from '../../services/LoadingService';
import { PageTitleService } from '../../services/PageTitle';
import { SwitchBoard } from '../../services/SwitchBoard';
import { StateService } from '../../services/StateService';
import { TaxPlanningService } from '../../tpiq-services/tax-planning.service';
import { FoundationInit } from '../../directives/foundation.directive';

export interface SubscriptionDetails {
  productName: string;
  subscriptionStatus: string;
  displayStatus: string;
  billingFrequency: string;
  termStartDate: string;
  termEndDate: string;
  nextBillingDate: string;
  lastPaymentAmount: number;
  canCancelRenewal: boolean;
  nonrenewalEndDate: string;
  subscriptionId?: number;
}

@Component({
  selector: 'app-manage-subscription',
  standalone: true,
  imports: [CommonModule, FormsModule, FoundationInit],
  templateUrl: './manage-subscription.component.html',
  styleUrl: './manage-subscription.component.scss'
})
export class ManageSubscriptionComponent implements OnInit, OnDestroy {
  org: string = '';
  userId: string = '';
  destroy$: EventEmitter<void> = new EventEmitter<void>();

  // Subscription data
  subscriptionData: SubscriptionDetails | null = null;
  loadError: string = '';

  // Do Not Renew flow
  showDoNotRenewSurvey: boolean = false;
  isSubmitting: boolean = false;
  submissionComplete: boolean = false;

  // Survey form
  primaryReason: string = '';
  additionalFeedback: string = '';

  // Dropdown options for "Why are you not renewing?"
  primaryReasonOptions: string[] = [
    'Customer support or service issue',
    'Onboarding or training wasn\'t clear enough',
    'Staff didn\'t buy in or have capacity',
    'Missing a feature or integration I needed',
    'Technical or reliability issue',
    'Payment or billing issues',
    'Signed up at the wrong time (too busy with deadlines)',
    'My client base wasn\'t a fit for tax planning',
    'I switched to a competitor',
    'No longer needed it (e.g. business sold or changed services)',
    'I want to decide when my current term ends'
  ];

  constructor(
    private router: Router,
    private toastService: ToastService,
    private loadingService: LoadingService,
    private titleService: PageTitleService,
    private switchBoard: SwitchBoard,
    private stateService: StateService,
    private taxPlanningService: TaxPlanningService
  ) {}

  ngOnInit(): void {
    this.org = Session.getOrg();
    const user: any = Session.getUser();
    this.userId = user?.id || '';

    this.titleService.setPageTitle('MANAGE SUBSCRIPTION');
    this.switchBoard.onPageChangeListener.next({});
    this.switchBoard.onToggleFeedback.next({
      display: true,
      pageName: 'Manage Subscription'
    });

    this.switchBoard.onClickPrev
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.gotoDefaultPage();
      });

    this.loadSubscriptionDetails();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadSubscriptionDetails(): void {
    this.loadingService.triggerLoadingEvent(true);
    this.loadError = '';

    this.taxPlanningService.getSubscriptionDetails(this.org, this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          if (response && response.data) {
            this.subscriptionData = response.data;
          } else {
            this.loadError = 'Unable to load subscription details';
          }
          this.loadingService.triggerLoadingEvent(false);
        },
        (error: any) => {
          this.loadingService.triggerLoadingEvent(false);
          let message = 'Failed to load subscription details';
          try {
            const parsed = typeof error === 'string' ? JSON.parse(error) : error;
            message = parsed?.message || message;
          } catch (e) {
            // ignore
          }
          this.loadError = message;
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      );
  }

  showDoNotRenewForm(): void {
    this.showDoNotRenewSurvey = true;
  }

  cancelDoNotRenew(): void {
    this.showDoNotRenewSurvey = false;
    this.primaryReason = '';
    this.additionalFeedback = '';
  }

  submitDoNotRenew(): void {
    this.isSubmitting = true;

    const request = {
      primaryReason: this.primaryReason,
      additionalFeedback: this.additionalFeedback
    };

    this.taxPlanningService.submitDoNotRenew(this.org, this.userId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response: any) => {
          this.isSubmitting = false;
          this.submissionComplete = true;
          this.toastService.pop(TOAST_TYPE.success, response?.message || 'Your request has been submitted');
          // Reload subscription data to reflect updated state
          this.loadSubscriptionDetails();
        },
        (error: any) => {
          this.isSubmitting = false;
          let message = 'Failed to process request. Please try again or contact support.';
          try {
            const parsed = typeof error === 'string' ? JSON.parse(error) : error;
            message = parsed?.message || message;
          } catch (e) {
            // ignore
          }
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      );
  }

  gotoDefaultPage(): void {
    const prevState = this.stateService.getPrevState();
    if (prevState?.url) {
      this.stateService.pop();
      this.router.navigate([prevState.url]);
    } else {
      this.router.navigate(['tpiq/dashboard']);
    }
  }

  goToDashboard(): void {
    this.router.navigate(['tpiq/dashboard']);
  }

  // Helper to format status for display
  getStatusDisplay(status: string): string {
    if (!status) return 'Unknown';
    if (status.toUpperCase() === 'NON_RENEWAL') return 'Non-Renewal';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  // Helper to format billing frequency
  getBillingFrequencyDisplay(frequency: string): string {
    if (!frequency) return '';
    const freq = frequency.toLowerCase();
    if (freq === 'yearly' || freq === 'annual' || freq === 'annually') return 'Yearly';
    if (freq === 'monthly') return 'Monthly';
    if (freq === 'quarterly') return 'Quarterly';
    return frequency;
  }

  // Helper to format date
  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  }

  // Helper to format currency
  formatAmount(amount: number): string {
    if (!amount) return '$0.00';
    // Amount is in cents
    return '$' + (amount / 100).toFixed(2);
  }

  // Check if subscription display status is active
  isActive(): boolean {
    return this.subscriptionData?.displayStatus?.toUpperCase() === 'ACTIVE';
  }

  // Check if subscription is set to non-renewal
  isNonRenewal(): boolean {
    return this.subscriptionData?.displayStatus?.toUpperCase() === 'NON_RENEWAL';
  }
}
